import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase';
import Header from '../components/Header';
import { 
  Pencil, Trash2, AlertTriangle, Share2, FilePlus, FilePenLine, FolderOpen, User, UserPlus
} from 'lucide-react';

// Reusable styled action card for consistency
const ActionCard = ({ icon: Icon, title, subtitle, color, onClick }) => (
  <div
    onClick={onClick}
    className="group cursor-pointer bg-slate-50 dark:bg-zinc-800/50 rounded-xl p-5 shadow-sm hover:shadow-lg hover:scale-[1.03] transition-all duration-300 ease-in-out"
  >
    <div className="flex items-center gap-4">
      <div
        className="w-11 h-11 flex items-center justify-center rounded-lg text-white"
        style={{ backgroundColor: color }}
      >
        <Icon size={20} />
      </div>
      <div>
        <h4 className="font-semibold text-gray-800 dark:text-white">{title}</h4>
        <p className="text-sm text-gray-500 dark:text-gray-400">{subtitle}</p>
      </div>
    </div>
  </div>
);

export default function OutpatientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [patient, setPatient] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    if (!id) return;
    async function fetchPatient() {
      const ref = doc(db, 'patients', id);
      const snap = await getDoc(ref);
      if (snap.exists()) {
        const data = snap.data();
        const fullName = [data.firstName, data.lastName].filter(Boolean).join(' ');
        setPatient({ id: snap.id, ...data, fullName });
      } else {
        console.log("No such document!");
        navigate('/dashboard');
      }
    }
    fetchPatient();
  }, [id, navigate]);

  const handleDelete = async () => {
    await deleteDoc(doc(db, 'patients', id));
    navigate('/dashboard');
  };

  const genderIcon = (sex) => {
    return sex?.toLowerCase() === 'female' 
      ? <UserPlus className="text-pink-400" size={22} />
      : <User className="text-sky-500" size={22} />;
  };

  const actionItems = [
    { icon: FilePlus, title: "New Visit Note", subtitle: "Document today's visit", color: "#22c55e", onClick: () => navigate(`/new-visit-note/${id}/new`) },
    { icon: FilePenLine, title: "Follow-up Visit", subtitle: "Document a subsequent visit", color: "#0ea5e9", onClick: () => navigate(`/follow-up-visit-note/${id}/new`) },
    { icon: FolderOpen, title: "Past Notes", subtitle: "View visit history", color: "#f97316", onClick: () => navigate(`/patients/${id}/notes`) },
    { icon: Share2, title: "Disposition Tools", subtitle: "Prescriptions & letters", color: "#8b5cf6", onClick: () => navigate(`/disposition-tools/${id}`) },
  ];

  return (
    <div className="bg-[#f4f7f9] dark:bg-black min-h-screen">
      <Header />
      <main className="max-w-4xl mx-auto py-10 px-4">
        {!patient ? (
          <div className="text-center p-10 text-gray-500">Loading Patient Details...</div>
        ) : (
          <div className="space-y-8">
            {/* --- PATIENT HEADER PANEL --- */}
            <section className="bg-white dark:bg-zinc-900 rounded-2xl shadow-sm p-8">
              <div className="flex justify-between items-start">
                <div className="flex items-start space-x-4">
                  <div className="bg-slate-100 dark:bg-zinc-700 p-3 rounded-full hidden sm:block">
                    {genderIcon(patient.sex)}
                  </div>
                  <div>
                    <h1 className="text-3xl font-extrabold text-gray-900 dark:text-white">
                      {patient.fullName}
                    </h1>
                    <p className="text-gray-500 mt-1 dark:text-gray-400">
                      CC: {patient.chiefComplaint}
                    </p>
                    <div className="flex items-center flex-wrap gap-x-4 gap-y-1 mt-4 text-sm text-gray-600 dark:text-gray-300">
                      <span>Age: {patient.age}</span>
                      <span className="hidden sm:inline">•</span>
                      <span>Sex: {patient.sex}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                   <span className="inline-block px-3 py-1 text-xs font-medium rounded-full bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300">
                    {patient.setting || 'Outpatient'}
                  </span>
                  <button
                    onClick={() => navigate(`/edit-patient/${id}`)}
                    title="Edit Patient"
                    className="p-2 rounded-full hover:bg-slate-200 dark:hover:bg-zinc-800 text-slate-500 transition-colors"
                  >
                    <Pencil size={18} />
                  </button>
                </div>
              </div>
            </section>

            {/* --- CLINIC ACTIONS PANEL --- */}
            <section className="bg-white dark:bg-zinc-900 rounded-2xl shadow-sm p-8">
              <h2 className="text-xl font-bold text-gray-800 dark:text-white mb-6">Clinic Actions</h2>
              {/* --- GRID LAYOUT UPDATED --- */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {actionItems.map(item => <ActionCard key={item.title} {...item} />)}
              </div>
            </section>

            {/* --- DANGER ZONE --- */}
            <section>
                <div className="bg-white dark:bg-zinc-900 rounded-2xl shadow-sm p-6 flex justify-between items-center">
                    <div>
                        <h3 className="font-semibold text-gray-800 dark:text-white">Delete Patient</h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400">This action is permanent and cannot be undone.</p>
                    </div>
                    <button
                        onClick={() => setShowConfirm(true)}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition"
                    >
                        <Trash2 className="inline-block mr-2" size={16} /> Delete
                    </button>
                </div>
            </section>
          </div>
        )}
      </main>

      {/* --- CONFIRM DELETE MODAL --- */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50 transition-opacity">
          <div className="bg-white dark:bg-zinc-800 p-8 rounded-2xl shadow-xl max-w-sm w-full mx-4">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/50">
                <AlertTriangle className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
              <h3 className="mt-5 text-lg font-semibold text-gray-900 dark:text-white">Delete Patient</h3>
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Are you sure? This action cannot be undone.</p>
            </div>
            <div className="mt-8 grid grid-cols-2 gap-4">
              <button onClick={() => setShowConfirm(false)} className="py-2.5 bg-slate-200 dark:bg-zinc-700 text-gray-800 dark:text-white rounded-lg font-semibold hover:bg-slate-300 dark:hover:bg-zinc-600 transition">Cancel</button>
              <button onClick={handleDelete} className="py-2.5 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}